import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Schema } from '../../services/api';
import type { AnnotationMode } from '../../services/schemaApi';
import { supportsStructuralInterpretation } from '../../lib/schemaTopology';

export interface AnnotateFormState {
  mode: AnnotationMode;
  guidance: string;
  instructions: string;
  /** Prepends template-validation-rules text to instructions when descriptions phase runs. */
  includeTemplateRules: boolean;
}

interface AnnotateDraftDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDraft: Schema | null;
  form: AnnotateFormState;
  onFormChange: (form: AnnotateFormState) => void;
  onSubmit: () => void;
  isLoading: boolean;
}

const MODE_OPTIONS: Array<{ value: AnnotationMode; label: string; help: string }> = [
  {
    value: 'full',
    label: 'Full annotation',
    help: 'Derive the structural surface (tables, identity, relationships) and then add descriptions and validation rules. Recommended.',
  },
  {
    value: 'structural',
    label: 'Structural only',
    help: 'Just derive tables, identity, and relationships from the template. Skip descriptions.',
  },
  {
    value: 'descriptions',
    label: 'Descriptions only',
    help: 'Refine descriptions and validation rules on the existing structure. Cheap to iterate on.',
  },
];

/**
 * Unified dialog for the schema-annotation phase. Replaces the
 * earlier "Enrich Metadata" + "Re-interpret Template" dialogs by
 * exposing a single ``mode`` selector that maps directly to the
 * backend's ``run_annotation(mode)`` contract.
 *
 * The dialog adapts to the draft's current state:
 *   * If the draft has no ``output_structure`` yet, ``mode='structural'``
 *     or ``mode='full'`` is the meaningful choice; ``descriptions``
 *     alone would be a no-op.
 *   * If the draft already has tables + output_structure, all three
 *     modes are valid; switching back to ``structural`` shows a
 *     re-interpret warning because that pass replaces the existing
 *     tables.
 */
const AnnotateDraftDialog: React.FC<AnnotateDraftDialogProps> = ({
  open,
  onClose,
  selectedDraft,
  form,
  onFormChange,
  onSubmit,
  isLoading,
}) => {
  const definition = selectedDraft?.definition;
  const hasOutputStructure = !!definition?.output_structure;
  const hasTables =
    !!definition?.tables && Object.keys(definition.tables).length > 0;
  const isReannotation = hasOutputStructure || hasTables;
  const templateSummary = definition?.template_summary;
  const canRunStructural = supportsStructuralInterpretation(definition);
  const modeOptions = canRunStructural
    ? MODE_OPTIONS
    : MODE_OPTIONS.filter((opt) => opt.value === 'descriptions');

  const setMode = (mode: AnnotationMode) =>
    onFormChange({ ...form, mode });
  const setGuidance = (guidance: string) =>
    onFormChange({ ...form, guidance });
  const setInstructions = (instructions: string) =>
    onFormChange({ ...form, instructions });
  const setIncludeTemplateRules = (includeTemplateRules: boolean) =>
    onFormChange({ ...form, includeTemplateRules });

  const runsStructural = form.mode === 'structural' || form.mode === 'full';
  const runsDescriptions = form.mode === 'descriptions' || form.mode === 'full';

  const handleSubmit = () => {
    onSubmit();
  };

  const submitLabel = (() => {
    if (form.mode === 'structural') return isReannotation ? 'Re-interpret' : 'Interpret template';
    if (form.mode === 'descriptions') return 'Refine descriptions';
    return isReannotation ? 'Re-annotate schema' : 'Annotate schema';
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Annotate Schema with AI</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            The AI agent annotates your schema in two phases — structural
            (tables, identity, relationships) and descriptive (column
            descriptions, validation rules). Pick which phases to run.
          </Typography>

          {/* Template summary chip strip */}
          {templateSummary && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Template summary
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`Format: ${(templateSummary.format || 'unknown').toUpperCase()}`}
                  size="small"
                  variant="outlined"
                />
                <Chip label={`Depth: ${templateSummary.depth ?? 0} levels`} size="small" variant="outlined" />
                <Chip label={`${templateSummary.element_count ?? 0} elements`} size="small" variant="outlined" />
                <Chip label={`${templateSummary.unique_elements ?? 0} unique`} size="small" variant="outlined" />
              </Stack>
            </Box>
          )}

          {/* Mode selector */}
          <FormControl>
            <FormLabel>What to do</FormLabel>
            <RadioGroup
              value={form.mode}
              onChange={(e) => setMode(e.target.value as AnnotationMode)}
            >
              {modeOptions.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {opt.help}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Re-annotation warning when structural phase will replace existing tables */}
          {runsStructural && isReannotation && (
            <Alert severity="warning">
              The structural phase will replace the existing tables and output
              structure. Any validation rules from a previous annotation will
              be cleared.
            </Alert>
          )}

          {/* Guidance for the structural phase */}
          {runsStructural && (
            <TextField
              label="Structural guidance (optional)"
              fullWidth
              multiline
              rows={3}
              value={form.guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder="E.g., 'This is a CDISC ODM 1.3 template for a cardiology trial', 'Ignore the Death forms', 'SubjectKey in my source data is patient_number'"
              helperText="Steers tables, identity columns, and relationships."
            />
          )}

          {/* Instructions + template-rules option for the descriptions phase */}
          {runsDescriptions && (
            <>
              {hasOutputStructure && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={form.includeTemplateRules}
                      onChange={(e) => setIncludeTemplateRules(e.target.checked)}
                    />
                  }
                  label="Include template-derived validation rules (Allowed Values, Cross-References, Length, Date Format)"
                />
              )}
              <TextField
                label="Description instructions (optional)"
                fullWidth
                multiline
                rows={3}
                value={form.instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="E.g., 'Add descriptions for customer tables', 'Add validation rules for email and phone fields'"
                helperText="Steers descriptions, validation rules, and sample-driven PK/FK inference. With template rules unchecked, you can still add or remove rules via instructions (e.g. 'Add validation rules on status')."
              />
            </>
          )}

          {!canRunStructural && (
            <Alert severity="info">
              This schema was uploaded as flat tables (SQL DDL or similar) — there is
              no template to interpret. Use <strong>Descriptions only</strong> to refine
              column metadata; structural interpretation is not available.
            </Alert>
          )}

          {/* Sanity nudge if operator picks descriptions-only on a draft with no structure yet */}
          {form.mode === 'descriptions' && !isReannotation && (
            <Alert severity="info">
              This draft has no tables yet — descriptions-only mode will be a
              no-op.
              {canRunStructural ? (
                <>
                  {' '}
                  Pick <strong>Full annotation</strong> or <strong>Structural only</strong> first.
                </>
              ) : (
                <>
                  {' '}
                  Upload or parse tables first, then run <strong>Descriptions only</strong>.
                </>
              )}
            </Alert>
          )}
        </Stack>
        <FormHelperText sx={{ mt: 2 }}>
          The agent may take 30–180 seconds depending on schema size.
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || (form.mode === 'descriptions' && !isReannotation)}
        >
          {isLoading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnotateDraftDialog;
